import { MsalAuthenticationTemplate, useAzureAuth } from "@datamole/wds-azure-auth";
import { useState, type ReactNode } from "react";

import { config } from "@/config";
import { AZURE_AD_INTERACTION_TYPE } from "@/features/auth";
import { AuthAzureErrorComponent } from "@/features/auth/AuthGuard/AuthAzureErrorComponent";
import { GetUser } from "@/features/auth/AuthGuard/GetUser";
import { LoginPage } from "@/pages/LoginPage";
import { WelcomePage } from "@/pages/WelcomePage";
import { useAppDispatch } from "@/store";
import { userLoggedOut } from "@/store/slices/auth.slice";

const authenticationRequest = {
    scopes: [config.AZURE_AUTH_SCOPE],
};

type Props = {
    children: ReactNode;
};

export const AuthGuard = ({ children }: Props) => {
    const [isErrorUserNotFound, setIsErrorUserNotFound] = useState(false);

    const dispatch = useAppDispatch();
    const { user: azureAuthUser, isInteractionInProgress, instance } = useAzureAuth();

    const resetUserNotFound = () => setIsErrorUserNotFound(false);

    const handleUserNotFound = async () => {
        try {
            instance.logoutRedirect({
                onRedirectNavigate: () => {
                    // Return false if you would like to stop navigation after local logout
                    setIsErrorUserNotFound(true);
                    dispatch(userLoggedOut());
                    return false;
                },
            });
        } catch {
            // ignore clearing errors and continue with logout flow
        }
    };

    // Azure Ad
    if (azureAuthUser || isInteractionInProgress) {
        return (
            <MsalAuthenticationTemplate
                interactionType={AZURE_AD_INTERACTION_TYPE}
                errorComponent={AuthAzureErrorComponent}
                loadingComponent={WelcomePage}
                authenticationRequest={authenticationRequest}
            >
                <GetUser addUserNotFound={handleUserNotFound} resetUserNotFound={resetUserNotFound}>
                    {children}
                </GetUser>
            </MsalAuthenticationTemplate>
        );
    }

    // Not authenticated
    return <LoginPage isErrorUserNotFound={isErrorUserNotFound} />;
};
